import React, { useState, useEffect, useCallback } from "react";
import { QRCodeSVG } from "qrcode.react";
import {
  X,
  CreditCard,
  Coins,
  Clock,
  Sparkles,
  CheckCircle2,
  Copy,
  Check,
  ExternalLink,
  ShieldCheck,
  Zap,
  Loader2,
} from "lucide-react";
import { GATEWAY_URL } from "@/lib/config";
import { getToken } from "@/lib/tokenStore";
import { getTransport } from "@/lib/transport";
import type { StreamPersonaTransport } from "@/lib/transport/types";
import type { BillingAlert } from "@/lib/transport/LiveKitTransport";

export interface TopUpCreditsModalProps {
  isOpen: boolean;
  onClose: () => void;
  transport?: StreamPersonaTransport | null;
}

interface CreditPackage {
  id: string;
  price: number;
  durationLabel: string;
  minutes: number;
  popular?: boolean;
  tierId: string;
}

const PACKAGES: CreditPackage[] = [
  { id: "pkg-5", price: 5, durationLabel: "~45 mins", minutes: 45, tierId: "lite_pass_45min" },
  { id: "pkg-15", price: 15, durationLabel: "~2.3 hrs", minutes: 138, popular: true, tierId: "lite_pass_138min" },
  { id: "pkg-30", price: 30, durationLabel: "~4.5 hrs", minutes: 270, tierId: "lite_pass_270min" },
  { id: "pkg-60", price: 60, durationLabel: "~9.1 hrs", minutes: 546, tierId: "lite_pass_546min" },
];

declare global {
  interface Window {
    PaystackPop?: {
      setup: (options: Record<string, unknown>) => {
        openIframe: () => void;
      };
    };
  }
}

export function TopUpCreditsModal({ isOpen, onClose, transport: customTransport }: TopUpCreditsModalProps) {
  const [selectedPkg, setSelectedPkg] = useState<CreditPackage>(PACKAGES[1]);
  const [paymentRail, setPaymentRail] = useState<"card" | "crypto">("card");
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // FX Rate state
  const [ngnRate, setNgnRate] = useState<number>(1550);

  // Crypto checkout state
  const [cryptoInvoice, setCryptoInvoice] = useState<{
    invoice_id: string;
    payment_address: string;
    expected_btc: number;
    expected_sats: number;
    expected_usd: number;
    status: string;
    expires_at?: string;
  } | null>(null);
  const [copied, setCopied] = useState(false);
  const [cryptoStatusText, setCryptoStatusText] = useState("Awaiting blockchain transaction...");

  // Success state on credit_success
  const [isSuccess, setIsSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("Top-up confirmed! Balance updated.");

  // Pre-fill email and license from token store
  useEffect(() => {
    const token = getToken();
    if (token?.licenseKey && !email) {
      setEmail(`license-${token.licenseKey.slice(0, 8)}@streampersona.app`);
    }
  }, [email]);

  // Load Paystack script dynamically
  useEffect(() => {
    if (!document.getElementById("paystack-inline-js")) {
      const script = document.createElement("script");
      script.id = "paystack-inline-js";
      script.src = "https://js.paystack.co/v1/inline.js";
      script.async = true;
      document.body.appendChild(script);
    }
  }, []);

  // Fetch FX rates
  useEffect(() => {
    async function fetchRates() {
      try {
        const res = await fetch(`${GATEWAY_URL}/api/v1/faceshift/pricing/rates`);
        if (res.ok) {
          const data = await res.json();
          if (data.effective_rate) {
            setNgnRate(data.effective_rate);
            return;
          }
        }
      } catch {
        // Use fallback 1550 NGN/USD
      }
    }
    if (isOpen) {
      fetchRates();
    }
  }, [isOpen]);

  // Listen to LiveKitTransport for real-time credit_success alert
  useEffect(() => {
    if (!isOpen) return;
    const activeTransport = customTransport || getTransport();
    if (!activeTransport?.onAlert) return;

    const unsub = activeTransport.onAlert((alert: BillingAlert) => {
      console.log("[TopUpCreditsModal] Alert received from transport:", alert);
      if (alert.type === "credit_success") {
        setIsSuccess(true);
        setSuccessMessage(alert.message || "Top-up confirmed! Balance updated.");
        // Automatically close modal after 2 seconds per spec
        const timer = setTimeout(() => {
          onClose();
          setIsSuccess(false);
          setCryptoInvoice(null);
        }, 2000);
        return () => clearTimeout(timer);
      }
    });

    return () => {
      unsub();
    };
  }, [isOpen, customTransport, onClose]);

  // Poll Crypto invoice status if crypto invoice is active
  useEffect(() => {
    if (!cryptoInvoice?.invoice_id || !isOpen || isSuccess) return;

    let mounted = true;
    const interval = setInterval(async () => {
      try {
        const res = await fetch(
          `${GATEWAY_URL}/api/v1/faceshift/checkout/crypto/status?invoice_id=${cryptoInvoice.invoice_id}`
        );
        if (!res.ok) return;
        const data = await res.json();
        if (!mounted) return;

        if (data.status === "detected") {
          setCryptoStatusText("Payment detected in mempool. Confirming on Bitcoin network...");
        } else if (data.status === "confirming") {
          setCryptoStatusText("Confirming block 1 of 2...");
        } else if (data.status === "confirmed") {
          setCryptoStatusText("Payment confirmed. Allocating credits...");
          setIsSuccess(true);
          setSuccessMessage("Payment confirmed! Your streaming credits are active.");
          window.dispatchEvent(new CustomEvent("sp_credits_updated"));
          clearInterval(interval);
          setTimeout(() => {
            if (mounted) {
              onClose();
              setIsSuccess(false);
              setCryptoInvoice(null);
            }
          }, 2000);
        }
      } catch (e) {
        console.warn("Error polling crypto status:", e);
      }
    }, 3500);

    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, [cryptoInvoice?.invoice_id, isOpen, isSuccess, onClose]);

  // Handle Paystack Card Checkout
  const handleCardCheckout = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    const token = getToken();
    const licenseKey = token?.licenseKey || "";
    const totalNgn = Math.round(selectedPkg.price * ngnRate);
    const amountKobo = totalNgn * 100;
    const userEmail = email.trim() || `user_${Date.now()}@streampersona.app`;

    try {
      // 1. Try Paystack Popup via JS SDK
      if (window.PaystackPop) {
        const publicKey =
          (import.meta as any).env?.VITE_PAYSTACK_PUBLIC_KEY ||
          "pk_live_64c3c3775c34d0fc2e32066016ccbab6fd15d57c";

        const handler = window.PaystackPop.setup({
          key: publicKey,
          email: userEmail,
          amount: amountKobo,
          currency: "NGN",
          metadata: {
            product: "streampersona",
            tier: selectedPkg.tierId,
            package_id: selectedPkg.id,
            license_key: licenseKey,
            minutes: selectedPkg.minutes,
            usd_equivalent: selectedPkg.price,
            custom_fields: [
              { display_name: "Product", variable_name: "product", value: "streampersona" },
              { display_name: "License Key", variable_name: "license_key", value: licenseKey },
              { display_name: "Minutes", variable_name: "minutes", value: selectedPkg.minutes.toString() },
            ],
          },
          callback: async (response: { reference: string }) => {
            setIsLoading(true);
            try {
              // Verify via platform-core endpoint
              const verifyRes = await fetch(
                `${GATEWAY_URL}/api/v1/faceshift/checkout/paystack/verify`,
                {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ reference: response.reference }),
                }
              );

              if (verifyRes.ok) {
                const verifyData = await verifyRes.json();
                if (verifyData.status === "success") {
                  setIsSuccess(true);
                  setSuccessMessage("Payment successful! Session credits loaded.");
                  window.dispatchEvent(new CustomEvent("sp_credits_updated"));
                  setTimeout(() => {
                    onClose();
                    setIsSuccess(false);
                  }, 2000);
                  return;
                }
              }
            } catch (err) {
              console.warn("Verification warning:", err);
            }
            // Fallback success acknowledgment
            setIsSuccess(true);
            setSuccessMessage("Payment submitted! Credits updating in real-time...");
            setTimeout(() => {
              onClose();
              setIsSuccess(false);
            }, 2000);
          },
          onClose: () => {
            setIsLoading(false);
          },
        });

        handler.openIframe();
        setIsLoading(false);
        return;
      }

      // 2. Direct API backend fallback
      const res = await fetch(`${GATEWAY_URL}/api/v1/faceshift/checkout/paystack`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: userEmail,
          payment_amount: selectedPkg.price,
          tier: selectedPkg.tierId,
          license_key: licenseKey,
        }),
      });

      if (!res.ok) {
        throw new Error(`Server returned ${res.status}`);
      }

      const data = await res.json();
      if (data.authorization_url) {
        window.open(data.authorization_url, "_blank");
      }
    } catch (err) {
      console.error("Paystack checkout error:", err);
      setError("Payment provider initializing. If popup was blocked, please re-click Pay.");
    } finally {
      setIsLoading(false);
    }
  }, [selectedPkg, ngnRate, email, onClose]);

  // Handle Crypto / BTCPay Server Checkout
  const handleCryptoCheckout = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    const token = getToken();
    const licenseKey = token?.licenseKey || "";
    const userEmail = email.trim() || `crypto_${Date.now()}@streampersona.app`;

    try {
      const res = await fetch(`${GATEWAY_URL}/api/v1/faceshift/checkout/crypto`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: userEmail,
          tier: selectedPkg.tierId,
          payment_amount: selectedPkg.price,
          license_key: licenseKey,
        }),
      });

      if (!res.ok) {
        throw new Error(`Failed to generate invoice (${res.status})`);
      }

      const invoice = await res.json();
      setCryptoInvoice({
        invoice_id: invoice.invoice_id,
        payment_address: invoice.payment_address || invoice.btc_address,
        expected_btc: invoice.expected_btc,
        expected_sats: invoice.expected_sats,
        expected_usd: invoice.expected_usd || selectedPkg.price,
        status: invoice.status || "pending",
        expires_at: invoice.expires_at,
      });
      setCryptoStatusText("Awaiting blockchain transaction broadcast...");
    } catch (err) {
      console.error("Crypto invoice error:", err);
      setError("Unable to generate Bitcoin invoice. Please try again or use Card payment.");
    } finally {
      setIsLoading(false);
    }
  }, [selectedPkg, email]);

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!isOpen) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md transition-opacity duration-200"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          background: "rgba(10, 10, 12, 0.94)",
          backdropFilter: "blur(24px)",
          WebkitBackdropFilter: "blur(24px)",
          boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.8), 0 0 0 1px rgba(255, 255, 255, 0.1)",
        }}
        className="relative w-full max-w-lg rounded-2xl border border-white/10 p-6 text-white overflow-hidden animate-in zoom-in-95 duration-200"
      >
        {/* Close Button */}
        <button
          type="button"
          onClick={onClose}
          aria-label="Close modal"
          className="absolute top-4 right-4 p-1.5 rounded-full text-stone-400 hover:text-white hover:bg-white/10 transition-colors cursor-pointer"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Success Overlay Animation on credit_success */}
        {isSuccess ? (
          <div className="flex flex-col items-center justify-center py-10 text-center animate-in zoom-in-75 duration-300">
            <div className="w-16 h-16 rounded-full bg-emerald-500/20 border-2 border-emerald-400 flex items-center justify-center mb-4 text-emerald-400 shadow-[0_0_30px_rgba(16,185,129,0.4)]">
              <CheckCircle2 className="w-9 h-9" />
            </div>
            <h3 className="text-xl font-bold text-emerald-300 mb-2">Top-Up Successful!</h3>
            <p className="text-sm text-stone-300 max-w-xs">{successMessage}</p>
            <span className="text-xs text-stone-500 mt-4">Closing automatically in a moment...</span>
          </div>
        ) : (
          <>
            {/* Modal Header */}
            <div className="flex items-center gap-2.5 mb-1.5">
              <div className="p-2 rounded-xl bg-accent-dim text-accent border border-accent/20">
                <Zap className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white tracking-tight flex items-center gap-2">
                  Top Up Streaming Credits
                </h2>
                <p className="text-xs text-stone-400">Keep your WebRTC video call active without interruption</p>
              </div>
            </div>

            {/* Rates Transparency Badge */}
            <div className="my-4 flex items-center justify-between px-3.5 py-2 rounded-xl bg-white/[0.04] border border-white/[0.08]">
              <div className="flex items-center gap-2 text-xs text-stone-300">
                <ShieldCheck className="w-4 h-4 text-accent" />
                <span>Transparent Cloud Compute Rates:</span>
              </div>
              <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-accent-dim text-accent text-xs font-semibold font-mono border border-accent/30">
                <Sparkles className="w-3 h-3" />
                <span>$6.58 / hr (~$0.11 / min)</span>
              </div>
            </div>

            {/* Package Selection Grid */}
            <div className="mb-4">
              <label className="text-xs font-semibold uppercase tracking-wider text-stone-400 block mb-2">
                Select Credit Package
              </label>
              <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4">
                {PACKAGES.map((pkg) => {
                  const isSelected = selectedPkg.id === pkg.id;
                  return (
                    <button
                      key={pkg.id}
                      type="button"
                      onClick={() => {
                        setSelectedPkg(pkg);
                        if (cryptoInvoice) setCryptoInvoice(null);
                      }}
                      className={`relative flex flex-col items-center p-3 rounded-xl border transition-all cursor-pointer text-center ${
                        isSelected
                          ? "bg-accent/15 border-accent shadow-[0_0_15px_rgba(0,212,170,0.25)]"
                          : "bg-white/[0.02] border-white/10 hover:border-white/20 hover:bg-white/[0.05]"
                      }`}
                    >
                      {pkg.popular && (
                        <span className="absolute -top-2 px-1.5 py-0.2 rounded-full bg-accent text-stone-950 text-[9px] font-extrabold uppercase tracking-wide">
                          Popular
                        </span>
                      )}
                      <div className="text-lg font-extrabold text-white font-mono">${pkg.price}</div>
                      <div className="flex items-center gap-1 text-[11px] text-stone-300 mt-0.5">
                        <Clock className="w-3 h-3 text-stone-400" />
                        <span>{pkg.durationLabel}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Payment Rails Tabs */}
            <div className="flex rounded-xl bg-white/[0.04] p-1 border border-white/10 mb-4">
              <button
                type="button"
                onClick={() => {
                  setPaymentRail("card");
                  setError(null);
                }}
                className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
                  paymentRail === "card"
                    ? "bg-stone-800 text-white shadow-sm border border-white/10"
                    : "text-stone-400 hover:text-stone-200"
                }`}
              >
                <CreditCard className="w-4 h-4" />
                <span>Credit / Debit Card</span>
              </button>

              <button
                type="button"
                onClick={() => {
                  setPaymentRail("crypto");
                  setError(null);
                }}
                className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
                  paymentRail === "crypto"
                    ? "bg-stone-800 text-white shadow-sm border border-white/10"
                    : "text-stone-400 hover:text-stone-200"
                }`}
              >
                <Coins className="w-4 h-4 text-amber-400" />
                <span>Bitcoin (BTCPay)</span>
              </button>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-3 px-3 py-2 rounded-lg bg-rose-950/50 border border-rose-500/40 text-rose-300 text-xs">
                {error}
              </div>
            )}

            {/* Rail 1: Credit / Debit Card (Paystack) */}
            {paymentRail === "card" && (
              <div className="space-y-3.5">
                <div className="p-3.5 rounded-xl bg-white/[0.02] border border-white/10 space-y-2 text-xs">
                  <div className="flex justify-between text-stone-300">
                    <span>Allocated Time:</span>
                    <span className="font-semibold text-white">{selectedPkg.durationLabel} of GPU streaming</span>
                  </div>
                  <div className="flex justify-between text-stone-300">
                    <span>USD Amount:</span>
                    <span className="font-mono font-bold text-white">${selectedPkg.price.toFixed(2)} USD</span>
                  </div>
                  <div className="flex justify-between text-stone-300 pt-1 border-t border-white/5">
                    <span>Local Currency Equivalent:</span>
                    <span className="font-mono font-bold text-accent">
                      ₦{(Math.round(selectedPkg.price * ngnRate)).toLocaleString()} NGN
                    </span>
                  </div>
                </div>

                <button
                  type="button"
                  disabled={isLoading}
                  onClick={handleCardCheckout}
                  className="w-full py-3 rounded-xl bg-accent hover:bg-accent-hover text-stone-950 font-bold text-sm transition-all shadow-[0_0_20px_rgba(0,212,170,0.3)] hover:scale-[1.01] active:scale-[0.99] flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Opening Secure Checkout...</span>
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-4 h-4" />
                      <span>Pay ${selectedPkg.price} via Card (Instant Top-Up)</span>
                    </>
                  )}
                </button>
              </div>
            )}

            {/* Rail 2: Cryptocurrency (BTCPay Server / SegWit) */}
            {paymentRail === "crypto" && (
              <div className="space-y-3">
                {!cryptoInvoice ? (
                  <div className="space-y-3">
                    <div className="p-3.5 rounded-xl bg-white/[0.02] border border-white/10 space-y-2 text-xs text-stone-300">
                      <div className="flex justify-between">
                        <span>Selected Pass:</span>
                        <span className="font-semibold text-white">${selectedPkg.price} ({selectedPkg.durationLabel})</span>
                      </div>
                      <p className="text-[11px] text-stone-400 leading-relaxed">
                        Generates a unique Native SegWit address powered by BTCPay Server. Real-time mempool
                        detection confirms credits automatically.
                      </p>
                    </div>

                    <button
                      type="button"
                      disabled={isLoading}
                      onClick={handleCryptoCheckout}
                      className="w-full py-3 rounded-xl bg-amber-500 hover:bg-amber-400 text-stone-950 font-bold text-sm transition-all shadow-[0_0_20px_rgba(245,158,11,0.3)] flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span>Generating Bitcoin Invoice...</span>
                        </>
                      ) : (
                        <>
                          <Coins className="w-4 h-4 fill-current" />
                          <span>Generate Bitcoin Address (${selectedPkg.price})</span>
                        </>
                      )}
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-col items-center space-y-3 text-center animate-in fade-in duration-200">
                    {/* QR Code */}
                    <div className="p-2.5 rounded-xl bg-white shadow-xl">
                      <QRCodeSVG
                        value={`bitcoin:${cryptoInvoice.payment_address}?amount=${cryptoInvoice.expected_btc}`}
                        size={140}
                        bgColor="#ffffff"
                        fgColor="#000000"
                      />
                    </div>

                    {/* Amount */}
                    <div className="flex items-baseline gap-2">
                      <span className="text-xl font-extrabold font-mono text-amber-400">
                        {cryptoInvoice.expected_btc} BTC
                      </span>
                      {cryptoInvoice.expected_sats && (
                        <span className="text-xs text-stone-400 font-mono">
                          ({cryptoInvoice.expected_sats.toLocaleString()} sats)
                        </span>
                      )}
                    </div>

                    {/* Deposit Address Box */}
                    <div className="w-full flex items-center gap-2 p-2 rounded-xl bg-white/[0.04] border border-white/10 text-left">
                      <span className="font-mono text-xs text-stone-300 truncate flex-1 px-1">
                        {cryptoInvoice.payment_address}
                      </span>
                      <button
                        type="button"
                        onClick={() => copyToClipboard(cryptoInvoice.payment_address)}
                        className="px-2.5 py-1 rounded-lg bg-stone-800 hover:bg-stone-700 text-white text-xs font-semibold flex items-center gap-1 cursor-pointer transition-colors"
                      >
                        {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                        <span>{copied ? "Copied" : "Copy"}</span>
                      </button>
                    </div>

                    {/* Status Pill */}
                    <div className="w-full py-1.5 px-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-300 text-xs font-medium flex items-center justify-center gap-2">
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      <span>{cryptoStatusText}</span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
